import { z } from 'zod'
import { asEnumType, asObjectType } from '@gqloom/zod'
import { numericSchema } from '@hapana/shared/backend/spanner'
import {
  createPaginationWithCursorArgsSchema,
  createPaginationWithCursorResultSchema,
} from '../database'
import {
  serviceCreditTemplateInputSchema,
  serviceCreditTemplateSchema,
} from './service-credit-template-schemas'
import { taxSettingSchema } from '../sites/site-tax-settings-schemas'
import { accessScheduleSchema } from '../site-doors/site-access-zone-schemas'

export const serviceTemplateTypeEnumSchema = z
  .enum(['PACKAGE', 'MEMBERSHIP'])
  .register(asEnumType, { name: 'ServiceTemplateType' })

export const serviceTemplateCategoryStatusEnumSchema = z
  .enum(['active', 'inactive'])
  .register(asEnumType, { name: 'ServiceTemplateCategoryStatus' })

export const serviceTemplateCategorySchema = z
  .object({
    siteId: z.string().uuid(),
    serviceTemplateCategoryId: z.string().uuid(),
    name: z.string().min(1).max(200),
    description: z.string().max(500).nullish(),
    status: serviceTemplateCategoryStatusEnumSchema,
    displayOrder: z.number().int().nullish(),
    createdAt: z.date(),
    createdBy: z.string().uuid(),
    updatedAt: z.date(),
    updatedBy: z.string().uuid().nullish(),
  })
  .register(asObjectType, {
    name: 'ServiceTemplateCategory',
    description: 'A category for organizing packages/service templates',
  })

export type ServiceTemplateCategorySchema = z.infer<typeof serviceTemplateCategorySchema>

export const createServiceTemplateCategoryInputSchema = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string().max(500).optional(),
    displayOrder: z.number().int().nonnegative().optional(),
    status: serviceTemplateCategoryStatusEnumSchema,
  })
  .register(asObjectType, {
    name: 'CreateServiceTemplateCategoryInput',
    description: 'Input for creating a new service template category',
  })

export const updateServiceTemplateCategoryInputSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(500).optional(),
    displayOrder: z.number().int().nonnegative().optional(),
    status: serviceTemplateCategoryStatusEnumSchema.optional(),
  })
  .register(asObjectType, {
    name: 'UpdateServiceTemplateCategoryInput',
    description: 'Input for updating a service template category',
  })

export type CreateServiceTemplateCategoryInput = z.infer<
  typeof createServiceTemplateCategoryInputSchema
>
export type UpdateServiceTemplateCategoryInput = z.infer<
  typeof updateServiceTemplateCategoryInputSchema
>

export const reorderServiceTemplateCategoriesInputSchema = z
  .object({
    serviceTemplateCategoryId: z.uuid(),
    displayOrder: z.int().nonnegative(),
  })
  .register(asObjectType, {
    name: 'ReorderServiceTemplateCategoryInput',
    description: 'Input for reordering a single service template category',
  })

export const reorderServiceTemplateCategoriesArgsSchema = z
  .object({
    categories: z.array(reorderServiceTemplateCategoriesInputSchema).min(1),
  })
  .register(asObjectType, {
    name: 'ReorderServiceTemplateCategoriesInput',
    description: 'Input containing array of categories with new display orders',
  })

export type ReorderServiceTemplateCategoriesInput = z.infer<
  typeof reorderServiceTemplateCategoriesInputSchema
>
export type ReorderServiceTemplateCategoriesArgs = z.infer<
  typeof reorderServiceTemplateCategoriesArgsSchema
>

export const serviceTemplateStatusEnumSchema = z
  .enum(['ACTIVE', 'ARCHIVED', 'DELETED', 'SCHEDULED'])
  .register(asEnumType, { name: 'ServiceTemplateStatus' })

export const firstPaymentAfterUnitEnumSchema = z
  .enum(['WEEKS', 'DAYS', 'MONTHS', 'ON_CYCLE'])
  .register(asEnumType, { name: 'FirstPaymentAfterUnit' })

export const FIRST_PAYMENT_DATE_TYPES = {
  AFTER: 'AFTER',
  ON_DATE: 'ON_DATE',
  ON_CYCLE: 'ON_CYCLE',
} as const

export const minimumTermAfterUnitEnumSchema = z
  .enum(['YEARS', 'MONTHS', 'WEEKS', 'DAYS'])
  .register(asEnumType, { name: 'MinimumTermAfterUnit' })

// Note: the ON_DATE credit allocation type is no longer surfaced in the Packages UI — it has been
// replaced by the Enable Pre-sale (futureStartDate) flow for scheduling future-dated credit access.
// ON_FIRST_USE is still used: a simple toggle in Packages lets operators defer credit expiration
// until the member's first credit use rather than purchase date.
export const firstCreditAllocationDateTypeEnumSchema = z
  .enum(['ON_FIRST_USE', 'ON_DATE'])
  .register(asEnumType, { name: 'FirstCreditAllocationDateType' })

export const paymentModeEnumSchema = z
  .enum(['ANY', 'CREDIT_CARD_ONLY', 'BANK_ONLY'])
  .register(asEnumType, { name: 'PaymentMode' })

export const autoRenewTypeEnumSchema = z
  .enum(['ON_CUSTOM_EXPIRATION_DATE', 'ON_TERM_DATE'])
  .register(asEnumType, { name: 'AutoRenewType' })

export const billingCycleUnitEnumSchema = z
  .enum(['YEARS', 'MONTHS', 'WEEKS'])
  .register(asEnumType, { name: 'BillingCycleUnit' })

const BILLING_DAY_MAX_BY_CYCLE_UNIT = {
  WEEKS: 7,
  MONTHS: 28,
  YEARS: 12,
} as const

const validateBillingDayByCycleUnit = (
  data: {
    billingDay?: number | null
    billingCycleUnit?: z.infer<typeof billingCycleUnitEnumSchema> | null
  },
  ctx: z.RefinementCtx
) => {
  if (data.billingDay == null || data.billingCycleUnit == null) {
    return
  }

  const maxBillingDay = BILLING_DAY_MAX_BY_CYCLE_UNIT[data.billingCycleUnit]
  if (data.billingDay <= maxBillingDay) {
    return
  }

  ctx.addIssue({
    code: 'custom',
    path: ['billingDay'],
    message: `billingDay must be between 1 and ${maxBillingDay} when billingCycleUnit is ${data.billingCycleUnit}`,
  })
}

export const lengthTypeEnumSchema = z
  .enum(['LIMITED', 'UNENDING'])
  .register(asEnumType, { name: 'ServiceLengthType' })

export const lengthUnitEnumSchema = z
  .enum(['YEARS', 'MONTHS', 'WEEKS', 'DAYS'])
  .register(asEnumType, { name: 'ServiceLengthUnit' })

export const autoConvertTriggerEnumSchema = z
  .enum(['ON_EXPIRY', 'ON_CREDIT_DEPLETION', 'NEVER'])
  .register(asEnumType, { name: 'AutoConvertTrigger' })

export const creditRolloverPolicyEnumSchema = z
  .enum(['ROLLOVER_ALL', 'ROLLOVER_UNUSED', 'NO_ROLLOVER'])
  .register(asEnumType, { name: 'CreditRolloverPolicy' })

// Needed to create a function to create separate objects, otherwise using the same object reference
// still caused collisions (seems to be caused by shared metadata)
const createGuestPassAllowanceSchema = () =>
  z.object({
    enabled: z.boolean(),
    newClientsOnlyEnabled: z.boolean(),
    amountPerCycle: z.number().int().min(1).nullable(),
  })

const guestPassAllowanceSchema = createGuestPassAllowanceSchema().register(asObjectType, {
  name: 'ServiceTemplateGuestPassAllowance',
})

const guestPassAllowanceInputSchema = z.object({
  enabled: z.boolean(),
  newClientsOnlyEnabled: z.boolean().optional(),
  amountPerCycle: z.number().int().min(1).optional(),
})

const createLimitSaleSchema = () =>
  z.object({
    perAccount: z.number().int().min(1).nullable(),
    perClient: z.number().int().min(1).nullable(),
  })

const limitSaleSchema = createLimitSaleSchema().register(asObjectType, {
  name: 'ServiceTemplateLimitSale',
})

const limitSaleInputSchema = z.object({
  perAccount: z.number().int().min(1).optional(),
  perClient: z.number().int().min(1).optional(),
})

const createFirstPaymentDateSchema = () =>
  z.object({
    enabled: z.boolean(),
    after: z
      .object({
        amount: z.number().int().min(1),
        unit: firstPaymentAfterUnitEnumSchema,
      })
      .nullable(),
    onDate: z.date().nullable(),
  })

const serviceFirstBillingDateSchema = createFirstPaymentDateSchema().register(asObjectType, {
  name: 'ServiceTemplateFirstPaymentDate',
})

const serviceFirstBillingDateInputSchema = z.object({
  enabled: z.boolean(),
  after: z
    .object({
      amount: z.number().int().min(1),
      unit: firstPaymentAfterUnitEnumSchema,
    })
    .optional(),
  onDate: z.coerce.date().optional(),
})

const createFirstCreditAllocationDateSchema = () =>
  z.object({
    enabled: z.boolean(),
    firstCreditAllocationDateType: firstCreditAllocationDateTypeEnumSchema.nullable(),
    onDate: z.date().nullable(),
  })

const firstCreditAllocationDateSchema = createFirstCreditAllocationDateSchema().register(
  asObjectType,
  {
    name: 'ServiceTemplateFirstCreditAllocationDate',
  }
)

const firstCreditAllocationDateInputSchema = z.object({
  enabled: z.boolean(),
  firstCreditAllocationDateType: firstCreditAllocationDateTypeEnumSchema.optional(),
  onDate: z.coerce.date().optional(),
})

const createCancellationSchema = () =>
  z.object({
    isEnabledForClient: z.boolean(),
    minimumTerm: z
      .object({
        enabled: z.boolean(),
        after: z
          .object({ amount: z.number().int().min(1), unit: minimumTermAfterUnitEnumSchema })
          .nullable(),
        charge: z
          .object({
            amount: numericSchema,
            isTaxEnabled: z.boolean(),
            taxSettingIds: z.array(taxSettingSchema.shape.taxSettingId),
          })
          .nullable(),
      })
      .nullable(),
  })

const cancellationSchema = createCancellationSchema().register(asObjectType, {
  name: 'ServiceTemplateCancellation',
})

const cancellationInputSchema = z.object({
  isEnabledForClient: z.boolean(),
  minimumTerm: z
    .object({
      enabled: z.boolean(),
      after: z
        .object({ amount: z.number().int().min(1), unit: minimumTermAfterUnitEnumSchema })
        .optional(),
      charge: z
        .object({
          amount: numericSchema,
          isTaxEnabled: z.boolean(),
          taxSettingIds: z.array(taxSettingSchema.shape.taxSettingId),
        })
        .optional(),
    })
    .optional(),
})

const createMultiplePaymentOptionsSchema = () =>
  z.object({
    payNowMode: paymentModeEnumSchema,
    recurringMode: paymentModeEnumSchema,
  })

const multiplePaymentOptionsSchema = createMultiplePaymentOptionsSchema().register(asObjectType, {
  name: 'ServiceTemplateMultiplePaymentOptions',
})

const multiplePaymentOptionsInputSchema = createMultiplePaymentOptionsSchema()

const createSecondaryRecurringFeeSchema = () =>
  z.object({
    enabled: z.boolean(),
    amount: numericSchema,
    taxEnabled: z.boolean(),
    taxSettingIds: z.array(taxSettingSchema.shape.taxSettingId).nullable(),
    serviceFirstBillingDate: serviceFirstBillingDateSchema.nullable(),
    billingCycleAmount: z.number().int().min(1),
    billingCycleUnit: billingCycleUnitEnumSchema,
  })

const secondaryRecurringFeeSchema = createSecondaryRecurringFeeSchema().register(asObjectType, {
  name: 'ServiceTemplateSecondaryRecurringFee',
})

const secondaryRecurringFeeInputSchema = z.object({
  enabled: z.boolean(),
  amount: numericSchema,
  taxEnabled: z.boolean().optional(),
  taxSettingIds: z.array(taxSettingSchema.shape.taxSettingId).optional(),
  serviceFirstBillingDate: serviceFirstBillingDateInputSchema.optional(),
  billingCycleAmount: z.number().int().min(1).optional(),
  billingCycleUnit: billingCycleUnitEnumSchema.optional(),
})

const accessScheduleJsonSchema = z.record(z.string(), z.unknown())

const validateAccessSchedule = (accessSchedule: unknown, ctx: z.RefinementCtx): void => {
  if (accessSchedule == null) {
    return
  }
  const parsed = accessScheduleSchema.safeParse(accessSchedule)
  if (!parsed.success) {
    ctx.addIssue({
      code: 'custom',
      path: ['accessSchedule'],
      message: 'accessSchedule must be a valid access schedule',
    })
  }
}

export const serviceTemplateSchema = z.object({
  serviceTemplateId: z.string(),
  siteId: z.string(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).nullable(),
  categoryId: z.string().nullable(),
  categoryName: z.string().nullable(),
  type: serviceTemplateTypeEnumSchema,
  status: serviceTemplateStatusEnumSchema,
  price: numericSchema,
  isIntroOffer: z.boolean(),
  isAllSharedSites: z.boolean(),
  sharedSiteIds: z.array(z.string()),
  taxEnabled: z.boolean(),
  taxSettingIds: z.array(taxSettingSchema.shape.taxSettingId).nullable(),
  onlineSaleEnabled: z.boolean(),
  onlineSaleStartDate: z.date().nullable(),
  changePlanEnabled: z.boolean(),
  minimumHomeUsageInDays: z.number().int().min(1).nullable(),
  expiryAlertInDays: z.number().int().min(1).nullable(),
  isEnabledForCMS: z.boolean(),
  isRestricted: z.boolean(),
  revenueTag: z.string().nullable(),
  sharingCreditsAmount: z.number().int().min(0).nullable(),
  dynamicDisplayServiceTemplateIds: z.array(z.string()).default([]),
  limitSale: limitSaleSchema.nullable(),
  clientOnlinePurchaseExpiryEnabled: z.boolean(),
  clientOnlinePurchaseExpiryDate: z.date().nullable(),
  serviceFirstBillingDate: serviceFirstBillingDateSchema.nullable(),
  firstCreditAllocationDate: firstCreditAllocationDateSchema.nullable(),
  cancellation: cancellationSchema.nullable(),
  multiplePaymentOptions: multiplePaymentOptionsSchema.nullable(),
  autoRenewEnabled: z.boolean().nullable(),
  autoRenewType: autoRenewTypeEnumSchema.nullable(),
  toServiceTemplateId: z.string().nullable(),
  isClientControlEnabled: z.boolean().nullable(),
  agreementEnabled: z.boolean().nullable(),
  agreementText: z.string().nullable(),
  siteAgreementTemplateId: z.string().nullable(),
  agreementSendConfirmation: z.boolean().nullable(),
  billingCycleAmount: z.number().int().min(1).nullable(),
  billingCycleUnit: billingCycleUnitEnumSchema.nullable(),
  lengthType: lengthTypeEnumSchema.nullable(),
  lengthAmount: z.number().int().min(1).nullable(),
  lengthUnit: lengthUnitEnumSchema.nullable(),
  oneTimeSetupFee: numericSchema.nullable(),
  oneTimeSetupFeeTaxEnabled: z.boolean().nullable(),
  oneTimeSetupFeeTaxSettingIds: z.array(taxSettingSchema.shape.taxSettingId).nullable(),
  isDefaultForTransferredClient: z.boolean().nullable(),
  autoConvertTo: z.string().nullable(), // For FITSTOP
  autoConvertTrigger: autoConvertTriggerEnumSchema.nullable(), //For FITSTOP
  creditRolloverPolicy: creditRolloverPolicyEnumSchema.nullable(), // Not for now
  inheritAgreementsOnConvert: z.boolean().nullable(), //Not for now
  billingDay: z.number().int().min(1).max(28).nullable(),
  secondaryRecurringFee: secondaryRecurringFeeSchema.nullable(),
  guestPassAllowance: guestPassAllowanceSchema.nullable(),
  displayOrder: z.number().int().nullish(),
  futureStartDate: z.date().nullable(),
  tierOrder: z.number().int().nullable().optional(),
  // CORE-1322 staging: nullable ref to a SiteAccountCode. Wired by PR2's resolver / template-edit flow.
  accountCodeId: z.string().nullish(),
  accessSchedule: accessScheduleJsonSchema.nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export const serviceTemplateCreateSchema = serviceTemplateSchema
  .omit({
    serviceTemplateId: true,
    status: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    description: z.string().max(500).optional(),
    categoryId: z.string().optional(),
    categoryName: z.string().optional(),
    taxSettingIds: z.array(taxSettingSchema.shape.taxSettingId).optional(),
    onlineSaleStartDate: z.coerce.date().nullable().optional(),
    changePlanEnabled: z.boolean().optional(),
    minimumHomeUsageInDays: z.number().int().min(1).nullable().optional(),
    expiryAlertInDays: z.number().int().min(1).nullable().optional(),
    revenueTag: z.string().nullable().optional(),
    sharingCreditsAmount: z.number().int().min(0).optional(),
    limitSale: limitSaleInputSchema.optional(),
    serviceFirstBillingDate: serviceFirstBillingDateInputSchema.optional(),
    clientOnlinePurchaseExpiryEnabled: z.boolean().optional(),
    clientOnlinePurchaseExpiryDate: z.coerce.date().nullable().optional(),
    firstCreditAllocationDate: firstCreditAllocationDateInputSchema.optional(),
    cancellation: cancellationInputSchema.optional(),
    multiplePaymentOptions: multiplePaymentOptionsInputSchema.optional(),
    displayOrder: z.number().int().nonnegative().optional(),
    tierOrder: z.number().int().nonnegative().optional(),
    autoRenewEnabled: z.boolean().optional(),
    autoRenewType: autoRenewTypeEnumSchema.optional(),
    toServiceTemplateId: z.string().optional(),
    isClientControlEnabled: z.boolean().optional(),
    agreementEnabled: z.boolean().optional(),
    agreementText: z.string().optional(),
    siteAgreementTemplateId: z.string().optional(),
    agreementSendConfirmation: z.boolean().optional(),
    billingCycleAmount: z.number().int().min(1).optional(),
    billingCycleUnit: billingCycleUnitEnumSchema.optional(),
    lengthType: lengthTypeEnumSchema.optional(),
    // CORE-1539: nullable so an UNENDING membership can explicitly clear
    // lengthAmount/lengthUnit. Without nullable on the create input schema
    // the front end couldn't push `null` to overwrite stale values from a
    // prior LIMITED configuration.
    lengthAmount: z.number().int().min(1).nullable().optional(),
    lengthUnit: lengthUnitEnumSchema.nullable().optional(),
    oneTimeSetupFee: numericSchema.optional(),
    oneTimeSetupFeeTaxEnabled: z.boolean().optional(),
    oneTimeSetupFeeTaxSettingIds: z.array(taxSettingSchema.shape.taxSettingId).optional(),
    isDefaultForTransferredClient: z.boolean().optional(),
    autoConvertTo: z.string().optional(),
    autoConvertTrigger: autoConvertTriggerEnumSchema.optional(),
    creditRolloverPolicy: creditRolloverPolicyEnumSchema.optional(),
    inheritAgreementsOnConvert: z.boolean().optional(),
    billingDay: z.number().int().min(1).max(28).nullable().optional(),
    secondaryRecurringFee: secondaryRecurringFeeInputSchema.optional(),
    guestPassAllowance: guestPassAllowanceInputSchema.optional(),
    credits: z.array(serviceCreditTemplateInputSchema).min(1).optional(),
    dynamicDisplayServiceTemplateIds: z.array(z.string()).optional(),
    futureStartDate: z.coerce.date().nullable().optional(),
    accessSchedule: accessScheduleJsonSchema.nullable().optional(),
  })
  .superRefine((data, ctx) => {
    validateBillingDayByCycleUnit(data, ctx)
    validateAccessSchedule(data.accessSchedule, ctx)

    if (data.futureStartDate != null) {
      const today = new Date()
      today.setUTCHours(0, 0, 0, 0)
      if (data.futureStartDate <= today) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['futureStartDate'],
          message: 'futureStartDate must be a future date',
        })
      }
    }

    // autoRenewType: ON_TERM_DATE can only be set for MEMBERSHIP, PACKAGE must use ON_CUSTOM_EXPIRATION_DATE
    if (data.type === 'PACKAGE' && data.autoRenewType === 'ON_TERM_DATE') {
      ctx.addIssue({
        code: 'custom',
        path: ['autoRenewType'],
        message: 'autoRenewType can only be ON_CUSTOM_EXPIRATION_DATE when type is PACKAGE',
      })
    }

    // BH-1268: ON_FIRST_USE is PACKAGE-only. Memberships have recurring billing cycles
    // where credits renew each cycle — "activate on first use" is not semantically valid.
    if (
      data.type === 'MEMBERSHIP' &&
      data.firstCreditAllocationDate?.firstCreditAllocationDateType &&
      data.firstCreditAllocationDate.firstCreditAllocationDateType !== 'ON_DATE'
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['firstCreditAllocationDate', 'firstCreditAllocationDateType'],
        message: 'firstCreditAllocationDateType can only be ON_DATE when type is MEMBERSHIP',
      })
    }

    // BH-1268: ON_FIRST_USE requires at least one credit with expiration enabled.
    // Without expiration, "activate on first use" has no effect — there's nothing to defer.
    if (
      data.firstCreditAllocationDate?.enabled === true &&
      data.firstCreditAllocationDate?.firstCreditAllocationDateType === 'ON_FIRST_USE' &&
      data.credits &&
      data.credits.length > 0 &&
      !data.credits.some((c: { expirationEnabled?: boolean }) => c.expirationEnabled)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['firstCreditAllocationDate', 'firstCreditAllocationDateType'],
        message:
          'ON_FIRST_USE requires at least one credit with expiration enabled. Without expiration configured, there is no expiration to defer to first use.',
      })
    }

    // autoRenew should not be set when type is MEMBERSHIP and lengthType is UNENDING
    if (data.type === 'MEMBERSHIP' && data.lengthType === 'UNENDING' && data.autoRenewEnabled) {
      ctx.addIssue({
        code: 'custom',
        path: ['autoRenewEnabled'],
        message:
          'autoRenew should not be enabled when type is MEMBERSHIP and lengthType is UNENDING',
      })
    }

    // cancellation should not be set when type is MEMBERSHIP and lengthType is UNENDING
    if (data.type === 'MEMBERSHIP' && data.lengthType === 'LIMITED' && data.cancellation) {
      ctx.addIssue({
        code: 'custom',
        path: ['cancellation'],
        message: 'cancellation should not be set when type is MEMBERSHIP and lengthType is LIMITED',
      })
    }

    // limitSale should not be set when autoRenewEnabled is true
    if (data.autoRenewEnabled && data.limitSale) {
      ctx.addIssue({
        code: 'custom',
        path: ['limitSale'],
        message: 'limitSale should not be set when autoRenew is enabled',
      })
    }

    // When autoRenewEnabled is true, autoRenewType and toServiceTemplateId are required
    if (data.autoRenewEnabled === true) {
      if (!data.autoRenewType) {
        ctx.addIssue({
          code: 'custom',
          path: ['autoRenewType'],
          message: 'autoRenewType is required when autoRenew is enabled',
        })
      }
      if (!data.toServiceTemplateId) {
        ctx.addIssue({
          code: 'custom',
          path: ['toServiceTemplateId'],
          message: 'toServiceTemplateId is required when autoRenew is enabled',
        })
      }
    }

    // When lengthType is LIMITED, lengthAmount and lengthUnit are required
    if (data.lengthType === 'LIMITED') {
      if (!data.lengthAmount) {
        ctx.addIssue({
          code: 'custom',
          path: ['lengthAmount'],
          message: 'lengthAmount is required when lengthType is LIMITED',
        })
      }
      if (!data.lengthUnit) {
        ctx.addIssue({
          code: 'custom',
          path: ['lengthUnit'],
          message: 'lengthUnit is required when lengthType is LIMITED',
        })
      }
    }

    // When lengthType is UNENDING, lengthAmount and lengthUnit should not be set
    if (data.lengthType === 'UNENDING') {
      if (data.lengthAmount) {
        ctx.addIssue({
          code: 'custom',
          path: ['lengthAmount'],
          message: 'lengthAmount should not be set when lengthType is UNENDING',
        })
      }
      if (data.lengthUnit) {
        ctx.addIssue({
          code: 'custom',
          path: ['lengthUnit'],
          message: 'lengthUnit should not be set when lengthType is UNENDING',
        })
      }
    }

    // When isAllSharedSites is true, sharedSiteIds should be empty
    if (data.isAllSharedSites && data.sharedSiteIds && data.sharedSiteIds.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['sharedSiteIds'],
        message: 'sharedSiteIds should be empty when isAllSharedSites is true',
      })
    }

    // clientOnlinePurchaseExpiryDate must be after onlineSaleStartDate when both are provided
    if (data.clientOnlinePurchaseExpiryDate && data.onlineSaleStartDate) {
      if (data.clientOnlinePurchaseExpiryDate <= data.onlineSaleStartDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['clientOnlinePurchaseExpiryDate'],
          message: 'clientOnlinePurchaseExpiryDate must be after onlineSaleStartDate',
        })
      }
    }

    // serviceFirstBillingDate validations
    if (data.serviceFirstBillingDate?.enabled === true) {
      const hasAfter = !!data.serviceFirstBillingDate.after
      const hasOnDate = !!data.serviceFirstBillingDate.onDate

      if (hasAfter && hasOnDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['serviceFirstBillingDate', 'after'],
          message: 'serviceFirstBillingDate.after should not be set when onDate is set',
        })
      }
    } else if (data.serviceFirstBillingDate?.enabled === false) {
      if (data.serviceFirstBillingDate.after) {
        ctx.addIssue({
          code: 'custom',
          path: ['serviceFirstBillingDate', 'after'],
          message: 'serviceFirstBillingDate.after should not be set when enabled is false',
        })
      }
      if (data.serviceFirstBillingDate.onDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['serviceFirstBillingDate', 'onDate'],
          message: 'serviceFirstBillingDate.onDate should not be set when enabled is false',
        })
      }
    }

    // firstCreditAllocationDate validations
    if (data.firstCreditAllocationDate?.enabled === true) {
      if (!data.firstCreditAllocationDate.firstCreditAllocationDateType) {
        ctx.addIssue({
          code: 'custom',
          path: ['firstCreditAllocationDate', 'firstCreditAllocationDateType'],
          message:
            'firstCreditAllocationDate.firstCreditAllocationDateType is required when enabled is true',
        })
      }

      if (
        data.firstCreditAllocationDate.firstCreditAllocationDateType === 'ON_DATE' &&
        !data.firstCreditAllocationDate.onDate
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['firstCreditAllocationDate', 'onDate'],
          message:
            'firstCreditAllocationDate.onDate is required when firstCreditAllocationDateType is ON_DATE',
        })
      }

      if (
        data.firstCreditAllocationDate.firstCreditAllocationDateType !== 'ON_DATE' &&
        data.firstCreditAllocationDate.onDate
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['firstCreditAllocationDate', 'onDate'],
          message:
            'firstCreditAllocationDate.onDate should not be set when firstCreditAllocationDateType is not ON_DATE',
        })
      }
    } else if (data.firstCreditAllocationDate?.enabled === false) {
      if (data.firstCreditAllocationDate.firstCreditAllocationDateType) {
        ctx.addIssue({
          code: 'custom',
          path: ['firstCreditAllocationDate', 'firstCreditAllocationDateType'],
          message:
            'firstCreditAllocationDate.firstCreditAllocationDateType should not be set when enabled is false',
        })
      }
      if (data.firstCreditAllocationDate.onDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['firstCreditAllocationDate', 'onDate'],
          message: 'firstCreditAllocationDate.onDate should not be set when enabled is false',
        })
      }
    }

    // cancellation.minimumTerm validations
    if (data.cancellation?.minimumTerm?.enabled === true) {
      if (!data.cancellation.minimumTerm.after) {
        ctx.addIssue({
          code: 'custom',
          path: ['cancellation', 'minimumTerm', 'after'],
          message: 'cancellation.minimumTerm.after is required when enabled is true',
        })
      }
    } else if (data.cancellation?.minimumTerm?.enabled === false) {
      if (data.cancellation.minimumTerm.after) {
        ctx.addIssue({
          code: 'custom',
          path: ['cancellation', 'minimumTerm', 'after'],
          message: 'cancellation.minimumTerm.after should not be set when enabled is false',
        })
      }
    }

    // agreement validations
    if (data.agreementEnabled === true && !data.agreementText) {
      ctx.addIssue({
        code: 'custom',
        path: ['agreementText'],
        message: 'agreementText is required when agreementEnabled is true',
      })
    }

    // serviceFirstBillingDate: either after or onDate must be set when enabled
    if (data.serviceFirstBillingDate?.enabled === true) {
      const hasAfter = !!data.serviceFirstBillingDate.after
      const hasOnDate = !!data.serviceFirstBillingDate.onDate

      if (!hasAfter && !hasOnDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['serviceFirstBillingDate'],
          message: 'Either after or onDate must be set when serviceFirstBillingDate is enabled',
        })
      }
    }

    // Membership-specific required fields
    if (data.type === 'MEMBERSHIP') {
      if (!data.billingCycleAmount) {
        ctx.addIssue({
          code: 'custom',
          path: ['billingCycleAmount'],
          message: 'billingCycleAmount is required when type is MEMBERSHIP',
        })
      }
      if (!data.billingCycleUnit) {
        ctx.addIssue({
          code: 'custom',
          path: ['billingCycleUnit'],
          message: 'billingCycleUnit is required when type is MEMBERSHIP',
        })
      }
      if (!data.lengthType) {
        ctx.addIssue({
          code: 'custom',
          path: ['lengthType'],
          message: 'lengthType is required when type is MEMBERSHIP',
        })
      }

      // Credits should not be set for MEMBERSHIP (they use membership-specific credit setup)
      if (data.credits && data.credits.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['credits'],
          message: 'credits array should contain at least one credit for MEMBERSHIP type',
        })
      }

      // Secondary recurring fee validations
      if (data.secondaryRecurringFee?.enabled === true) {
        if (!data.secondaryRecurringFee.billingCycleAmount) {
          ctx.addIssue({
            code: 'custom',
            path: ['secondaryRecurringFee', 'billingCycleAmount'],
            message: 'secondaryRecurringFee.billingCycleAmount is required when enabled is true',
          })
        }
        if (!data.secondaryRecurringFee.billingCycleUnit) {
          ctx.addIssue({
            code: 'custom',
            path: ['secondaryRecurringFee', 'billingCycleUnit'],
            message: 'secondaryRecurringFee.billingCycleUnit is required when enabled is true',
          })
        }
      }

      // Guest pass allowance validations
      if (data.guestPassAllowance?.enabled === true) {
        if (!data.guestPassAllowance.amountPerCycle) {
          ctx.addIssue({
            code: 'custom',
            path: ['guestPassAllowance', 'amountPerCycle'],
            message: 'guestPassAllowance.amountPerCycle is required when enabled is true',
          })
        }
      }
    }

    // Package-specific validations
    if (data.type === 'PACKAGE') {
      // For PACKAGE, recurringMode must be ANY (cannot be changed)
      if (
        data.multiplePaymentOptions?.recurringMode &&
        data.multiplePaymentOptions.recurringMode !== 'ANY'
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['multiplePaymentOptions', 'recurringMode'],
          message: 'recurringMode can only be changed for MEMBERSHIP type, must be ANY for PACKAGE',
        })
      }

      // billingCycle fields should not be set for PACKAGE
      if (data.billingCycleAmount !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['billingCycleAmount'],
          message: 'billingCycleAmount should only be set for MEMBERSHIP type',
        })
      }
      if (data.billingCycleUnit !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['billingCycleUnit'],
          message: 'billingCycleUnit should only be set for MEMBERSHIP type',
        })
      }
    }
  })

/**
 * Bulk-import wrapper input for ServiceTemplate.
 *
 * Differs from {@link serviceTemplateCreateSchema} (the UI write path's
 * per-row input) in one way: the caller MUST attach `serviceTemplateId`
 * (deterministic from the transformer, required for retry idempotency and so
 * the child credits can reference it before the parent write completes). All
 * other shape and `.superRefine` validations carry over unchanged.
 *
 * Consumers: `ServiceTemplatesService.bulkCreateForImport` (the wrapper) and
 * `ServiceTemplatesRepository.bulkUpsertForImport` (the repo bulk method that
 * runs the Spanner write).
 */
export const serviceTemplateBulkCreateForImportSchema = serviceTemplateCreateSchema.safeExtend({
  serviceTemplateId: z.string(),
})

export type ServiceTemplateBulkCreateForImportSchema = z.infer<
  typeof serviceTemplateBulkCreateForImportSchema
>

export const serviceTemplateUpdateSchema = serviceTemplateSchema
  .omit({
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    onlineSaleStartDate: z.coerce.date().nullable().optional(),
    limitSale: limitSaleInputSchema.nullable().optional(),
    serviceFirstBillingDate: serviceFirstBillingDateInputSchema.optional(),
    firstCreditAllocationDate: firstCreditAllocationDateInputSchema.optional(),
    cancellation: cancellationInputSchema.optional(),
    multiplePaymentOptions: multiplePaymentOptionsInputSchema.optional(),
    secondaryRecurringFee: secondaryRecurringFeeInputSchema.optional(),
    guestPassAllowance: guestPassAllowanceInputSchema.optional(),
    credits: z.array(serviceCreditTemplateInputSchema).min(1).optional(),
    dynamicDisplayServiceTemplateIds: z.array(z.string()).optional(),
    clientOnlinePurchaseExpiryEnabled: z.boolean().optional(),
    clientOnlinePurchaseExpiryDate: z.coerce.date().nullable().optional(),
    futureStartDate: z.coerce.date().nullable().optional(),
    accessSchedule: accessScheduleJsonSchema.nullable().optional(),
  })
  .partial()
  .superRefine((data, ctx) => {
    validateBillingDayByCycleUnit(data, ctx)
    validateAccessSchedule(data.accessSchedule, ctx)

    if (data.futureStartDate != null) {
      const today = new Date()
      today.setUTCHours(0, 0, 0, 0)
      if (data.futureStartDate <= today) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['futureStartDate'],
          message: 'futureStartDate must be a future date',
        })
      }
    }

    // autoRenewType: ON_TERM_DATE can only be set for MEMBERSHIP, PACKAGE must use ON_CUSTOM_EXPIRATION_DATE
    if (data.type === 'PACKAGE' && data.autoRenewType === 'ON_TERM_DATE') {
      ctx.addIssue({
        code: 'custom',
        path: ['autoRenewType'],
        message: 'autoRenewType can only be ON_CUSTOM_EXPIRATION_DATE when type is PACKAGE',
      })
    }

    // BH-1268: ON_FIRST_USE is PACKAGE-only. Memberships have recurring billing cycles
    // where credits renew each cycle — "activate on first use" is not semantically valid.
    if (
      data.type === 'MEMBERSHIP' &&
      data.firstCreditAllocationDate?.firstCreditAllocationDateType &&
      data.firstCreditAllocationDate.firstCreditAllocationDateType !== 'ON_DATE'
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['firstCreditAllocationDate', 'firstCreditAllocationDateType'],
        message: 'firstCreditAllocationDateType can only be ON_DATE when type is MEMBERSHIP',
      })
    }

    // BH-1268: ON_FIRST_USE requires at least one credit with expiration enabled.
    // Without expiration, "activate on first use" has no effect — there's nothing to defer.
    if (
      data.firstCreditAllocationDate?.enabled === true &&
      data.firstCreditAllocationDate?.firstCreditAllocationDateType === 'ON_FIRST_USE' &&
      data.credits &&
      data.credits.length > 0 &&
      !data.credits.some((c: { expirationEnabled?: boolean }) => c.expirationEnabled)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['firstCreditAllocationDate', 'firstCreditAllocationDateType'],
        message:
          'ON_FIRST_USE requires at least one credit with expiration enabled. Without expiration configured, there is no expiration to defer to first use.',
      })
    }

    // autoRenew should not be set when type is MEMBERSHIP and lengthType is UNENDING
    if (data.type === 'MEMBERSHIP' && data.lengthType === 'UNENDING' && data.autoRenewEnabled) {
      ctx.addIssue({
        code: 'custom',
        path: ['autoRenewEnabled'],
        message:
          'autoRenew should not be enabled when type is MEMBERSHIP and lengthType is UNENDING',
      })
    }

    // cancellation should not be set when type is MEMBERSHIP and lengthType is UNENDING
    if (data.type === 'MEMBERSHIP' && data.lengthType === 'LIMITED' && data.cancellation) {
      ctx.addIssue({
        code: 'custom',
        path: ['cancellation'],
        message: 'cancellation should not be set when type is MEMBERSHIP and lengthType is LIMITED',
      })
    }

    // limitSale should not be set when autoRenewEnabled is true
    if (data.autoRenewEnabled && data.limitSale) {
      ctx.addIssue({
        code: 'custom',
        path: ['limitSale'],
        message: 'limitSale should not be set when autoRenew is enabled',
      })
    }

    // When autoRenewEnabled is true, autoRenewType and toServiceTemplateId are required
    if (data.autoRenewEnabled === true) {
      if (!data.autoRenewType) {
        ctx.addIssue({
          code: 'custom',
          path: ['autoRenewType'],
          message: 'autoRenewType is required when autoRenew is enabled',
        })
      }
      if (!data.toServiceTemplateId) {
        ctx.addIssue({
          code: 'custom',
          path: ['toServiceTemplateId'],
          message: 'toServiceTemplateId is required when autoRenew is enabled',
        })
      }
    }

    // When lengthType is LIMITED, lengthAmount and lengthUnit are required
    if (data.lengthType === 'LIMITED') {
      if (!data.lengthAmount) {
        ctx.addIssue({
          code: 'custom',
          path: ['lengthAmount'],
          message: 'lengthAmount is required when lengthType is LIMITED',
        })
      }
      if (!data.lengthUnit) {
        ctx.addIssue({
          code: 'custom',
          path: ['lengthUnit'],
          message: 'lengthUnit is required when lengthType is LIMITED',
        })
      }
    }

    // When lengthType is UNENDING, lengthAmount and lengthUnit should not be set
    if (data.lengthType === 'UNENDING') {
      if (data.lengthAmount) {
        ctx.addIssue({
          code: 'custom',
          path: ['lengthAmount'],
          message: 'lengthAmount should not be set when lengthType is UNENDING',
        })
      }
      if (data.lengthUnit) {
        ctx.addIssue({
          code: 'custom',
          path: ['lengthUnit'],
          message: 'lengthUnit should not be set when lengthType is UNENDING',
        })
      }
    }

    // When isAllSharedSites is true, sharedSiteIds should be empty
    if (data.isAllSharedSites && data.sharedSiteIds && data.sharedSiteIds.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['sharedSiteIds'],
        message: 'sharedSiteIds should be empty when isAllSharedSites is true',
      })
    }

    // clientOnlinePurchaseExpiryDate must be after onlineSaleStartDate when both are provided
    if (data.clientOnlinePurchaseExpiryDate && data.onlineSaleStartDate) {
      if (data.clientOnlinePurchaseExpiryDate <= data.onlineSaleStartDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['clientOnlinePurchaseExpiryDate'],
          message: 'clientOnlinePurchaseExpiryDate must be after onlineSaleStartDate',
        })
      }
    }

    // serviceFirstBillingDate validations
    if (data.serviceFirstBillingDate?.enabled === true) {
      const hasAfter = !!data.serviceFirstBillingDate.after
      const hasOnDate = !!data.serviceFirstBillingDate.onDate

      if (hasAfter && hasOnDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['serviceFirstBillingDate', 'after'],
          message: 'serviceFirstBillingDate.after should not be set when onDate is set',
        })
      }
    } else if (data.serviceFirstBillingDate?.enabled === false) {
      if (data.serviceFirstBillingDate.after) {
        ctx.addIssue({
          code: 'custom',
          path: ['serviceFirstBillingDate', 'after'],
          message: 'serviceFirstBillingDate.after should not be set when enabled is false',
        })
      }
      if (data.serviceFirstBillingDate.onDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['serviceFirstBillingDate', 'onDate'],
          message: 'serviceFirstBillingDate.onDate should not be set when enabled is false',
        })
      }
    }

    // firstCreditAllocationDate validations
    if (data.firstCreditAllocationDate?.enabled === true) {
      if (!data.firstCreditAllocationDate.firstCreditAllocationDateType) {
        ctx.addIssue({
          code: 'custom',
          path: ['firstCreditAllocationDate', 'firstCreditAllocationDateType'],
          message:
            'firstCreditAllocationDate.firstCreditAllocationDateType is required when enabled is true',
        })
      }

      if (
        data.firstCreditAllocationDate.firstCreditAllocationDateType === 'ON_DATE' &&
        !data.firstCreditAllocationDate.onDate
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['firstCreditAllocationDate', 'onDate'],
          message:
            'firstCreditAllocationDate.onDate is required when firstCreditAllocationDateType is ON_DATE',
        })
      }

      if (
        data.firstCreditAllocationDate.firstCreditAllocationDateType !== 'ON_DATE' &&
        data.firstCreditAllocationDate.onDate
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['firstCreditAllocationDate', 'onDate'],
          message:
            'firstCreditAllocationDate.onDate should not be set when firstCreditAllocationDateType is not ON_DATE',
        })
      }
    } else if (data.firstCreditAllocationDate?.enabled === false) {
      if (data.firstCreditAllocationDate.firstCreditAllocationDateType) {
        ctx.addIssue({
          code: 'custom',
          path: ['firstCreditAllocationDate', 'firstCreditAllocationDateType'],
          message:
            'firstCreditAllocationDate.firstCreditAllocationDateType should not be set when enabled is false',
        })
      }
      if (data.firstCreditAllocationDate.onDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['firstCreditAllocationDate', 'onDate'],
          message: 'firstCreditAllocationDate.onDate should not be set when enabled is false',
        })
      }
    }

    // cancellation.minimumTerm validations
    if (data.cancellation?.minimumTerm?.enabled === true) {
      if (!data.cancellation.minimumTerm.after) {
        ctx.addIssue({
          code: 'custom',
          path: ['cancellation', 'minimumTerm', 'after'],
          message: 'cancellation.minimumTerm.after is required when enabled is true',
        })
      }
    } else if (data.cancellation?.minimumTerm?.enabled === false) {
      if (data.cancellation.minimumTerm.after) {
        ctx.addIssue({
          code: 'custom',
          path: ['cancellation', 'minimumTerm', 'after'],
          message: 'cancellation.minimumTerm.after should not be set when enabled is false',
        })
      }
    }

    // agreement validations
    if (data.agreementEnabled === true && !data.agreementText) {
      ctx.addIssue({
        code: 'custom',
        path: ['agreementText'],
        message: 'agreementText is required when agreementEnabled is true',
      })
    }

    // serviceFirstBillingDate: either after or onDate must be set when enabled
    if (data.serviceFirstBillingDate?.enabled === true) {
      const hasAfter = !!data.serviceFirstBillingDate.after
      const hasOnDate = !!data.serviceFirstBillingDate.onDate

      if (!hasAfter && !hasOnDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['serviceFirstBillingDate'],
          message: 'Either after or onDate must be set when serviceFirstBillingDate is enabled',
        })
      }
    }

    // Membership-specific required fields
    if (data.type === 'MEMBERSHIP') {
      if (!data.billingCycleAmount) {
        ctx.addIssue({
          code: 'custom',
          path: ['billingCycleAmount'],
          message: 'billingCycleAmount is required when type is MEMBERSHIP',
        })
      }
      if (!data.billingCycleUnit) {
        ctx.addIssue({
          code: 'custom',
          path: ['billingCycleUnit'],
          message: 'billingCycleUnit is required when type is MEMBERSHIP',
        })
      }
      if (!data.lengthType) {
        ctx.addIssue({
          code: 'custom',
          path: ['lengthType'],
          message: 'lengthType is required when type is MEMBERSHIP',
        })
      }

      // Credits should not be set for MEMBERSHIP (they use membership-specific credit setup)
      if (data.credits && data.credits.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['credits'],
          message: 'credits array should contain at least one credit for MEMBERSHIP type',
        })
      }

      // Secondary recurring fee validations
      if (data.secondaryRecurringFee?.enabled === true) {
        if (!data.secondaryRecurringFee.billingCycleAmount) {
          ctx.addIssue({
            code: 'custom',
            path: ['secondaryRecurringFee', 'billingCycleAmount'],
            message: 'secondaryRecurringFee.billingCycleAmount is required when enabled is true',
          })
        }
        if (!data.secondaryRecurringFee.billingCycleUnit) {
          ctx.addIssue({
            code: 'custom',
            path: ['secondaryRecurringFee', 'billingCycleUnit'],
            message: 'secondaryRecurringFee.billingCycleUnit is required when enabled is true',
          })
        }
      }

      // Guest pass allowance validations
      if (data.guestPassAllowance?.enabled === true) {
        if (!data.guestPassAllowance.amountPerCycle) {
          ctx.addIssue({
            code: 'custom',
            path: ['guestPassAllowance', 'amountPerCycle'],
            message: 'guestPassAllowance.amountPerCycle is required when enabled is true',
          })
        }
      }
    }

    // Package-specific validations
    if (data.type === 'PACKAGE') {
      // For PACKAGE, recurringMode must be ANY (cannot be changed)
      if (
        data.multiplePaymentOptions?.recurringMode &&
        data.multiplePaymentOptions.recurringMode !== 'ANY'
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['multiplePaymentOptions', 'recurringMode'],
          message: 'recurringMode can only be changed for MEMBERSHIP type, must be ANY for PACKAGE',
        })
      }

      // billingCycle fields should not be set for PACKAGE
      if (data.billingCycleAmount !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['billingCycleAmount'],
          message: 'billingCycleAmount should only be set for MEMBERSHIP type',
        })
      }
      if (data.billingCycleUnit !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['billingCycleUnit'],
          message: 'billingCycleUnit should only be set for MEMBERSHIP type',
        })
      }
    }
  })

export const serviceTemplatePublicSchema = serviceTemplateSchema
  .safeExtend({
    credits: z.array(serviceCreditTemplateSchema).optional(),
  })
  .register(asObjectType, {
    name: 'ServiceTemplate',
    description: 'Defines a sellable service (package or membership)',
  })

export type ServiceTemplateSchema = z.infer<typeof serviceTemplateSchema>
export type ServiceTemplateCreateSchema = z.infer<typeof serviceTemplateCreateSchema>
export type ServiceTemplateUpdateSchema = z.infer<typeof serviceTemplateUpdateSchema>
export type ServiceTemplatePublicSchema = z.infer<typeof serviceTemplatePublicSchema>

export const serviceTemplateOrderByEnumSchema = z
  .enum(['name', 'price', 'createdAt', 'updatedAt', 'status', 'type'])
  .register(asEnumType, {
    name: 'ServiceTemplateOrderBy',
    description: 'Fields available for ordering service templates',
  })

export const serviceTemplatePaginationArgsSchema = createPaginationWithCursorArgsSchema()
  .withSearch()
  .withOrderBy(serviceTemplateOrderByEnumSchema, [])
  .build()
  .extend({
    siteId: z.string().optional(),
    siteIds: z.array(z.string()).optional(),
    type: serviceTemplateTypeEnumSchema.optional(),
    status: serviceTemplateStatusEnumSchema.optional(),
    categoryId: z.string().optional(),
    isIntroOffer: z.boolean().optional(),
  })

export const serviceTemplatePaginationResultSchema = createPaginationWithCursorResultSchema(
  serviceTemplatePublicSchema
)

export type ServiceTemplatePaginationArgsSchema = z.infer<
  typeof serviceTemplatePaginationArgsSchema
>
export type ServiceTemplatePaginationResultSchema = z.infer<
  typeof serviceTemplatePaginationResultSchema
>
